import type { JSX } from 'react';
import { Seo } from '@/components/seo';
import { ActionCard } from '@/components/dashboard/ActionCard';
import { DashboardOverviewStats } from '@/components/dashboard/overview/DashboardOverviewStats';
import { DashboardActionQueue } from '@/components/dashboard/overview/DashboardActionQueue';
import { EnvelopeOpenIcon, PlusCircledIcon, ChatBubbleIcon } from '@radix-ui/react-icons';

export function ManagerOperations(): JSX.Element {
  return (
    <section className="flex flex-col gap-xl py-8" aria-labelledby="manager-operations-title">
      <Seo
        title="Leasing Operations Hub"
        description="Monitor available units, leads in pipeline, and tenant service SLAs from a single workspace. Preview upcoming tooling for Strashin property managers."
        canonicalPath="/dashboard"
        noIndex
        openGraph={{ image: '/logo.jpg' }}
      />
      <header className="flex flex-col gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-brand-accent">
          Daily Leasing Ops
        </p>
        <h1
          id="manager-operations-title"
          className="text-3xl font-semibold text-brand-primary md:text-4xl"
        >
          Leasing Operations Hub
        </h1>
        <p className="max-w-3xl text-sm text-text-primary/70 md:text-base">
          Keep tabs on every available unit, see which tours are scheduled, and prioritize leads
          that need follow-up. Service level dashboards will focus on vacancy readiness and tour
          conversion so your team can respond before momentum slips.
        </p>
      </header>

      <DashboardOverviewStats />
      <DashboardActionQueue />

      <div className="grid gap-4 md:grid-cols-3">
        <ActionCard
          title="Capture lead"
          description="Create a new leasing prospect and set the next outreach before handoff."
          to="/dashboard/leads"
          icon={EnvelopeOpenIcon}
          badge="Pipeline"
          ctaLabel="Open leads"
        />
        <ActionCard
          title="Add unit"
          description="Select a property and add a new unit with pricing and availability details."
          to="/dashboard/properties"
          icon={PlusCircledIcon}
          badge="Inventory"
          ctaLabel="Add unit"
        />
        <ActionCard
          title="Review properties"
          description="Inspect property readiness, marketing assets, and availability without leaving the dashboard."
          to="/dashboard/properties"
          icon={ChatBubbleIcon}
          badge="Portfolio"
          ctaLabel="Open properties"
        />
      </div>
    </section>
  );
}
